<script>
// Menu em cascata (hover) que renderiza recursivamente a árvore de pastas do
// AAWZ Drive. Cada pasta com subpastas vira um v-menu aninhado, abrindo o
// próximo nível à direita; pastas-folha viram um item simples. Um clique em
// qualquer pasta emite `select` com o nó — o pai decide o que fazer (enviar
// direto ou abrir o modal de renomear). Auto-referência via `name` para
// permitir o uso recursivo do próprio componente no template.
//
// Controle de abertura é MANUAL (:value + mouseenter/mouseleave próprios),
// não open-on-hover: o conteúdo do v-menu é teletransportado pelo Vuetify
// para fora da árvore DOM do menu pai (portal) — cada nível aninhado acaba
// numa posição diferente do DOM, então hovering um nível descendente sai da
// bounding box do wrapper do nível ANCESTRAL e dispara o mouseleave dele
// (nem sempre é o próprio nível que "erra": é o pai que perde o containment
// nativo). Sem correção, isso agenda o fechamento do ancestral enquanto o
// usuário ainda interage 2+ níveis abaixo — quando o timer do ancestral
// dispara, ele desmonta seu conteúdo e derruba junto todos os descendentes,
// mesmo que cada um deles ache que continua "aberto".
//
// A correção usa `$emit('active')`, que trafega pela árvore de COMPONENTES
// Vue (imune a onde o Vuetify posicionou o DOM). Qualquer hover — ativador,
// wrapper de conteúdo ou item-folha, em qualquer profundidade — cancela o
// closeTimer local (notifyActive) E reemite 'active' pro pai, que faz o
// mesmo. A cadeia sobe nível a nível até a raiz (chat-message-actions.vue),
// cancelando o closeTimer de CADA ancestral no caminho ativo.
const CLOSE_DELAY_MS = 300

export default {
  name: 'AawzDriveFolderPickerMenu',
  props: {
    folders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      openFolderId: null,
      closeTimer: null,
    }
  },
  beforeDestroy() {
    this.cancelClose()
  },
  methods: {
    subfoldersOf(folder) {
      const children = (folder && folder.children) || []
      return children.filter(
        (node) => node && (node.is_folder || node.type === 'folder')
      )
    },
    hasSubfolders(folder) {
      return this.subfoldersOf(folder).length > 0
    },
    onSelect(folder) {
      this.$emit('select', folder)
    },
    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    openSubmenu(folderId) {
      this.cancelClose()
      this.openFolderId = folderId
    },
    // Hover no ativador: abre o submenu localmente E avisa os ancestrais
    // (via bubbling de 'active') que este ramo está ativo.
    onActivatorEnter(folderId) {
      this.openSubmenu(folderId)
      this.$emit('active')
    },
    // Hover em qualquer ponto do conteúdo (wrapper de nível filho, ou sinal
    // borbulhado de um descendente): cancela o fechamento LOCAL e propaga
    // 'active' pro próprio pai, continuando a cadeia até a raiz.
    notifyActive() {
      this.cancelClose()
      this.$emit('active')
    },
    scheduleClose() {
      this.cancelClose()
      this.closeTimer = setTimeout(() => {
        this.openFolderId = null
      }, CLOSE_DELAY_MS)
    },
    // Sincroniza com fechamentos que o próprio Vuetify decide (click fora,
    // Esc) — sem isso, :value reafirmaria true no próximo render e o menu
    // pareceria "não fechar" ao clicar fora.
    onMenuInput(folderId, isOpen) {
      if (!isOpen && this.openFolderId === folderId) {
        this.openFolderId = null
      }
    },
  },
}
</script>

<template>
  <v-list class="az-list-no-icon-spacing py-1" dense>
    <template v-for="folder in folders">
      <v-menu
        v-if="hasSubfolders(folder)"
        :key="folder.id"
        :value="openFolderId === folder.id"
        offset-x
        right
        nudge-right="4"
        content-class="aawz-drive-folder-picker-menu"
        @input="onMenuInput(folder.id, $event)"
      >
        <template v-slot:activator="{ attrs }">
          <v-list-item
            v-bind="attrs"
            @click="onSelect(folder)"
            @mouseenter="onActivatorEnter(folder.id)"
            @mouseleave="scheduleClose"
          >
            <v-list-item-title>{{ folder.name }}</v-list-item-title>
            <icon-svg name="mdi-chevron-right" size="16" color="#8E8E8E" />
          </v-list-item>
        </template>
        <div @mouseenter="notifyActive" @mouseleave="scheduleClose">
          <AawzDriveFolderPickerMenu
            :folders="subfoldersOf(folder)"
            @select="onSelect"
            @active="notifyActive"
          />
        </div>
      </v-menu>
      <v-list-item
        v-else
        :key="folder.id"
        @click="onSelect(folder)"
        @mouseenter="$emit('active')"
      >
        <v-list-item-title>{{ folder.name }}</v-list-item-title>
      </v-list-item>
    </template>
  </v-list>
</template>

<style lang="sass">
// Estilo do conteúdo teletransportado do v-menu (content-class não é escopado
// pelo componente). Duplicado (não importado) de .chat-actions-menu do módulo
// atendimento de propósito — aawz-drive não deve depender de estilo de outro
// módulo; visual idêntico, dono local.
.aawz-drive-folder-picker-menu
  border-radius: 8px !important
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important
  overflow: hidden

  .v-list-item
    min-height: 36px
    cursor: pointer
</style>

<style lang="sass" scoped>
// O wrapper de ativação do v-menu (<div class="v-menu v-menu--inline">) fica
// como IRMÃO do v-list-item dentro do v-list — é 0x0 mas ainda participa do
// layout (ex.: gap do flex), inflando o espaçamento entre itens reais.
// display:contents remove da árvore de layout; não afeta o conteúdo
// teletransportado do menu (renderizado em outro lugar do DOM via
// content-class) nem o ativador (Vuetify mede o v-list-item em si, não este
// wrapper, para posicionar o conteúdo).
::v-deep .v-menu.v-menu--inline
  display: contents
</style>
